import { useCallback, useEffect, useState } from "react";
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Grid, Alert
} from "@mui/material";
import { Localized } from "@fluent/react";
import { useAppDispatch } from "../../../../app/hooks";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { AppTextFieldHandler } from "../../../../app/appTypes";
import {
    WorkflowRole
} from "../api/workflowTypes";
import { createWorkflowRole, updateWorkflowRole } from "../api/workflowRolesApi";


export interface WorkflowRoleDialogProps {
    workflowRole?: WorkflowRole; 
    onSucces: (workflowRole: WorkflowRole) => void;
    onCancel: () => void;
}

export const WorkflowRoleDialog = ({ workflowRole, onSucces, onCancel }: WorkflowRoleDialogProps) => {
    const dispatch = useAppDispatch();

    const [name, setName] = useState<string>(workflowRole?.name ?? "");
    const [code, setCode] = useState<string>(workflowRole?.code ?? "");
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleNameChange: AppTextFieldHandler = useCallback((e) => {
        setName(e.target.value);
    }, []);

    const handleCodeChange: AppTextFieldHandler = useCallback((e) => {
        setCode(e.target.value);
    }, []);

    const handleSaveClick = async () => {
        if (!name || !code) return;

        setSaving(true);
        try {
            if (workflowRole) {
                const result = await dispatch(updateWorkflowRole({ id: workflowRole.id, data: { name, code } }));
                if (updateWorkflowRole.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"));
                    onSucces(result.payload);
                } else {
                    setServerError("update-error");
                }
            } else {
                const result = await dispatch(createWorkflowRole({ name, code }));
                if (createWorkflowRole.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"));
                    onSucces(result.payload);
                } else {
                    setServerError("create-error");
                }
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} fullWidth maxWidth='sm'>
            <DialogTitle>
                <Localized id={workflowRole ? "edit-workflow-role" : "create-workflow-role"}>
                    {workflowRole ? "Edytuj rolę" : "Nowa rola WFL"}
                </Localized>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label={<Localized id="workflow-role-name">Nazwa roli</Localized>}
                            value={name}
                            onChange={handleNameChange}
                            error={name === ""}
                            helperText={name === "" && <Localized id="required"><span>Wymagane</span></Localized>}
                            inputProps={{ maxLength: 50 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label={<Localized id="workflow-role-code">Kod roli</Localized>}
                            value={code}
                            onChange={handleCodeChange}
                            error={code === ""}
                            helperText={code === "" && <Localized id="required"><span>Wymagane</span></Localized>}
                            inputProps={{ maxLength: 50 }}
                        />
                    </Grid>
                </Grid>

                {serverError && (
                    <Alert sx={{ marginTop: 2 }} severity="error" onClose={() => setServerError("")}>
                        <Localized id={serverError}><span>Wystąpił błąd</span></Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
